import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBullseye,
  FaCheck,
  FaEye,
  FaHandshake,
  FaStar,
} from "react-icons/fa";

import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";

// Sized + compressed at the CDN. The original asset is several MB.
const TEAM_IMAGE =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1000&q=80";

const STATS = [
  { value: "100+", label: "Projects Delivered" },
  { value: "50+", label: "Clients Served" },
  { value: "15+", label: "Years Experience" },
  { value: "6", label: "Industries" },
];

const CREDENTIALS = [
  "Programme & project delivery leadership",
  "Enterprise integration and cloud modernization",
  "Regulated-industry experience end to end",
  "Digital transformation from strategy to adoption",
];

const PURPOSE = [
  {
    title: "Our Mission",
    Icon: FaBullseye,
    text: "To help organizations successfully deliver technology, transformation, and digital initiatives that create measurable business value.",
  },
  {
    title: "Our Vision",
    Icon: FaEye,
    text: "To be a trusted partner for organizations seeking innovation, operational excellence, and sustainable business growth.",
  },
  {
    title: "Our Values",
    Icon: FaHandshake,
    text: "Integrity, collaboration, innovation, customer focus, and delivery excellence are at the heart of everything we do.",
  },
];

function About() {
  return (
    <>
      <CustomNavbar />

      <style>{`
        .abt {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }

        /* --- Hero --- */
        .abt-hero {
          position: relative; overflow: hidden;
          padding: clamp(56px, 7vw, 92px) 0 clamp(48px, 6vw, 78px);
          background:
            radial-gradient(44rem 26rem at 82% 4%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 46%, #fff8f5 100%);
          color: #0f172a;
        }
        .abt-hero__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(80px); opacity: .55;
          width: 24rem; height: 24rem; bottom: -9rem; left: -7rem;
          background: radial-gradient(circle, #fff3ec, transparent 70%);
        }
        .abt-hero__badge {
          display: inline-flex; align-items: center; gap: 9px;
          background: #ffffffdd; color: var(--peach-ink);
          padding: 10px 24px; border-radius: 999px;
          font-weight: 700; font-size: .8rem; letter-spacing: .12em;
          text-transform: uppercase;
          box-shadow: 0 10px 24px rgba(194,65,12,.2);
        }
        .abt-hero__title {
          margin-top: 24px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.9rem, 5vw, 3.3rem); line-height: 1.12; color: #0f172a;
        }
        .abt-hero__title em {
          font-style: normal;
          background: linear-gradient(96deg, #c2410c 0%, #ff6b35 55%, #b91c1c 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .abt-hero__rule {
          width: 88px; height: 5px; border-radius: 999px; margin: 22px auto 0;
          background: linear-gradient(90deg, var(--peach-deep), #b91c1c);
        }
        .abt-hero__lead {
          max-width: 750px; margin: 22px auto 0;
          color: #4a3a33; font-size: clamp(1rem, 1.8vw, 1.1rem); line-height: 1.85;
        }

        /* Stat strip straddling the hero's lower edge */
        .abt-stats {
          position: relative; z-index: 2;
          margin-top: clamp(34px, 4vw, 52px);
          background: #fff; border-radius: 20px;
          box-shadow: 0 20px 46px rgba(194,65,12,.16);
          padding: clamp(20px, 3vw, 30px) clamp(12px, 2vw, 24px);
        }
        .abt-stat { text-align: center; }
        .abt-stat__v {
          font-weight: 800; line-height: 1.1;
          font-size: clamp(1.5rem, 3.2vw, 2.2rem);
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .abt-stat__l { color: #64748b; font-size: .82rem; }

        /* --- Who we are --- */
        .abt-who { padding: clamp(56px, 7vw, 92px) 0; background: #fff; }
        .abt-who__frame { position: relative; }
        .abt-who__img {
          width: 100%; height: auto; display: block;
          border-radius: 24px; box-shadow: 0 24px 50px rgba(15,23,42,.18);
          /* Lifts the photo above the plate. z-index:-1 on the plate would
             instead push it behind the section's opaque white background. */
          position: relative; z-index: 1;
        }
        /* Decorative peach plate, offset down-right behind the photo */
        .abt-who__plate {
          position: absolute; inset: 22px -22px -22px 22px;
          border-radius: 24px;
          background: linear-gradient(135deg, var(--peach), var(--peach-soft));
          opacity: .55;
        }
        .abt-who__ribbon {
          position: absolute; right: -10px; bottom: 22px; z-index: 2;
          display: inline-flex; align-items: center; gap: 8px;
          background: #0f172a; color: var(--peach-soft);
          padding: 12px 20px; border-radius: 14px;
          font-weight: 700; font-size: .84rem;
          box-shadow: 0 14px 30px rgba(15,23,42,.35);
        }
        .abt-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--peach-tint); color: var(--peach-ink);
          padding: 9px 20px; border-radius: 999px;
          font-weight: 700; font-size: .78rem; letter-spacing: .1em;
        }
        .abt-h2 {
          margin-top: 20px; color: #0f172a; font-weight: 800;
          font-size: clamp(1.6rem, 3.2vw, 2.5rem);
          line-height: 1.18; letter-spacing: -.02em;
        }
        .abt-p { color: #64748b; line-height: 1.9; margin-top: 18px; }
        .abt-list { list-style: none; padding: 0; margin: 22px 0 0; }
        .abt-list li {
          display: flex; align-items: flex-start; gap: 11px;
          color: #334155; font-size: .95rem; margin-bottom: 11px;
        }
        .abt-tick {
          flex-shrink: 0; margin-top: 3px;
          width: 21px; height: 21px; border-radius: 50%;
          display: grid; place-items: center; font-size: .58rem; color: #fff;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
        }
        .abt-cta {
          display: inline-flex; align-items: center; gap: 10px;
          margin-top: 26px; text-decoration: none;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; font-weight: 700; padding: 13px 26px; border-radius: 14px;
          box-shadow: 0 12px 28px rgba(255,107,53,.36);
          transition: transform .22s ease, box-shadow .22s ease;
        }
        .abt-cta:hover, .abt-cta:focus-visible {
          color: #fff; transform: translateY(-3px);
          box-shadow: 0 18px 38px rgba(255,107,53,.5);
        }
        .abt-cta svg { transition: transform .22s ease; }
        .abt-cta:hover svg { transform: translateX(5px); }

        /* --- Purpose --- */
        /* Ends on white so the footer's peach CTA panel, which overlaps up
           into this section, still reads against its background. */
        .abt-purpose {
          padding: clamp(56px, 7vw, 92px) 0 clamp(80px, 9vw, 120px);
          background: linear-gradient(180deg, #fff8f5 0%, #ffeee6 45%, #ffffff 100%);
        }
        .abt-card {
          position: relative; overflow: hidden; height: 100%;
          background: #fff; border-radius: 20px; padding: 30px 26px;
          border: 1px solid #f3e5dd;
          box-shadow: 0 8px 26px rgba(194,65,12,.08);
          transition: transform .26s ease, box-shadow .26s ease, border-color .26s ease;
        }
        .abt-card::before {
          content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
          transform: scaleX(0); transform-origin: left;
          transition: transform .34s cubic-bezier(.22,1,.36,1);
        }
        .abt-card__icon {
          width: 60px; height: 60px; border-radius: 18px; margin-bottom: 18px;
          display: grid; place-items: center;
          background: var(--peach-tint); color: var(--peach-deep);
          transition: background .26s ease, color .26s ease, transform .26s ease;
        }
        .abt-card__title {
          color: #0f172a; font-weight: 700; font-size: 1.18rem; margin-bottom: 10px;
        }
        .abt-card__text { color: #64748b; line-height: 1.8; margin: 0; font-size: .95rem; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the card scrolls through the middle of the screen. */
        .abt-card:hover,
        .abt-card.is-live {
          transform: translateY(-8px);
          box-shadow: 0 24px 48px rgba(194,65,12,.18);
          border-color: var(--peach-soft);
        }
        .abt-card:hover::before,
        .abt-card.is-live::before { transform: scaleX(1); }
        .abt-card:hover .abt-card__icon,
        .abt-card.is-live .abt-card__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #fff; transform: scale(1.06);
        }
        @media (prefers-reduced-motion: reduce) {
          .abt-card, .abt-card::before, .abt-card__icon, .abt-cta { transition: none; }
          .abt-card:hover, .abt-cta:hover { transform: none; }
        }
      `}</style>

      <div className="abt">
        {/* Hero */}
        <section className="abt-hero">
          <span className="abt-hero__blob" />
          <Container className="text-center" style={{ position: "relative", zIndex: 1 }}>
            <span className="abt-hero__badge" data-aos="fade-up">
              <FaStar />
              About Sarvavya
            </span>

            {/* theme.css hides <br> in section headings under 576px — the
                explicit space stops the two halves concatenating. */}
            <h1 className="abt-hero__title" data-aos="fade-up" data-aos-delay="70">
              Transforming Ideas Into
              <br />{" "}
              <em>Business Success</em>
            </h1>

            <div className="abt-hero__rule" />

            <p className="abt-hero__lead" data-aos="fade-up" data-aos-delay="140">
              Delivering technology excellence, digital transformation, programme
              leadership, and strategic consulting services that empower
              organizations to achieve sustainable growth.
            </p>

            <div className="abt-stats" data-aos="fade-up" data-aos-delay="200">
              <Row className="g-3">
                {STATS.map((s) => (
                  <Col xs={6} md={3} key={s.label}>
                    <div className="abt-stat">
                      <div className="abt-stat__v">{s.value}</div>
                      <div className="abt-stat__l">{s.label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Container>
        </section>

        {/* Who we are */}
        <section className="abt-who">
          <Container>
            <Row className="align-items-center g-5">
              <Col lg={6} data-aos="fade-right">
                <div className="abt-who__frame">
                  <span className="abt-who__plate" />
                  <img
                    src={TEAM_IMAGE}
                    alt="The Sarvavya consulting team collaborating"
                    className="abt-who__img"
                    width={1000}
                    height={667}
                    loading="lazy"
                  />
                  <span className="abt-who__ribbon">
                    <FaStar />
                    15+ years of delivery
                  </span>
                </div>
              </Col>

              <Col lg={6} data-aos="fade-left">
                <span className="abt-badge">WHO WE ARE</span>

                <h2 className="abt-h2">
                  Your Trusted Technology &amp; Transformation Partner
                </h2>

                <p className="abt-p">
                  Sarvavya is a specialist consultancy delivering project
                  management, programme leadership, enterprise technology
                  solutions, digital transformation, website development, and
                  digital marketing services across multiple industries.
                </p>

                <p className="abt-p">
                  Our consultants bring extensive experience across Banking,
                  Payments, Social Housing, Education, Retail and Non-Profit
                  sectors — delivering large-scale business transformations,
                  regulatory programmes, and technology modernization.
                </p>

                <ul className="abt-list">
                  {CREDENTIALS.map((c) => (
                    <li key={c}>
                      <span className="abt-tick">
                        <FaCheck />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>

                <Link to="/services" className="abt-cta">
                  Explore Our Services
                  <FaArrowRight />
                </Link>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Mission / Vision / Values */}
        <section className="abt-purpose">
          <Container>
            <div className="text-center mb-5" data-aos="fade-up">
              <span className="abt-badge">OUR PURPOSE</span>
              <h2 className="abt-h2">Mission, Vision &amp; Values</h2>
              <p className="abt-p mx-auto" style={{ maxWidth: "700px" }}>
                Guided by innovation, integrity, and delivery excellence in every
                engagement.
              </p>
            </div>

            <Row className="g-4">
              {PURPOSE.map(({ title, Icon, text }, i) => (
                <Col lg={4} md={6} key={title} data-aos="fade-up" data-aos-delay={i * 90}>
                  <div className="abt-card" data-live>
                    <span className="abt-card__icon">
                      <Icon size={24} />
                    </span>
                    <h3 className="abt-card__title">{title}</h3>
                    <p className="abt-card__text">{text}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default About;
